import {
  AdvancedEditCut,
  TranscriptSegment,
  WordCorrection,
} from '@app/routes/content/routes/recordings/domain';

import { generateWordsFromSegment } from './generate-words';

/**
 * The flat word list — the core data structure driving ALL rendering in
 * the EditTranscript component.
 */
export type FlatWord = {
  /** Raw index in transcript.segments[]. */
  segmentIndex: number;
  /** Index in segment.words[]. */
  wordIndex: number;
  /** Word timing/text payload. */
  word: {
    word: string;
    start: number; // seconds
    end: number; // seconds
    score: number;
  };
  /**
   * True when the word does NOT fall within any of the included cut regions
   * → render with strikethrough. The cuts array defines what to KEEP, so a
   * word is removed/struck-through when it has no overlap with any cut.
   */
  isCut: boolean;
  /** Replacement text from wordCorrections, if present. */
  correction?: string;
};

/**
 * Builds the flat word list used by the EditTranscript renderer and selection
 * logic.
 *
 * Time comparison convention:
 *   - Cut times are stored in milliseconds (start_time_ms / end_time_ms).
 *   - Word times are in seconds (start / end).
 *   - The cuts array defines INCLUDED regions (segments to keep).
 *   - A word is "cut" (struck-through) when it does NOT overlap with any
 *     included cut region — i.e. it falls inside a gap between cuts.
 *   - Overlap test (any-overlap): word is IN a cut when
 *       word.start * 1000 < cut.end_time_ms  &&  word.end * 1000 > cut.start_time_ms
 *   - Therefore isCut = !cuts.some(overlapTest)
 */
export const buildFlatWords = (
  segments: TranscriptSegment[],
  cuts: AdvancedEditCut[],
  wordCorrections: WordCorrection[],
): FlatWord[] => {
  const flatWords: FlatWord[] = [];

  for (let si = 0; si < segments.length; si++) {
    const segment = segments[si];
    const words = generateWordsFromSegment(segment);

    for (let wi = 0; wi < words.length; wi++) {
      const word = words[wi];
      const wordStartMs = word.start * 1000;
      const wordEndMs = word.end * 1000;

      // A word is "cut" when it has NO overlap with any included cut region.
      // cuts.some(...) returns true if the word is INCLUDED → negate for isCut.
      const isCut = !cuts.some(
        cut => wordStartMs < cut.end_time_ms && wordEndMs > cut.start_time_ms,
      );

      const correctionEntry = wordCorrections.find(
        wc => wc.segmentIndex === si && wc.wordIndex === wi,
      );

      flatWords.push({
        segmentIndex: si,
        wordIndex: wi,
        word,
        isCut,
        correction: correctionEntry?.word,
      });
    }
  }

  return flatWords;
};
